import { Fragment, ReactNode } from 'react';

import { GAME_MODE, NotesSection, songBeat } from '~/interfaces';
import styles, { playerTextColor } from '~/modules/game-engine/drawing/styles';
import GameState from '~/modules/game-engine/game-state/game-state';
import PlayersManager from '~/modules/players/players-manager';
import isNotesSection from '~/modules/songs/utils/is-notes-section';
import { getFirstNoteStartFromSections, getLastNoteEnd } from '~/modules/songs/utils/notes-selectors';
import { getTrackSingers } from '~/routes/game/singing/game-overlay/helpers/pass-the-mic-turn';
import { cn } from '~/utils/cn';

import AnimatedLine from './animated-line';
import { BreakAnnouncement, BreakProgress, BreakRest, SingingOverNotice } from './band-notices';
import Headstart from './headstart';
import LyricNoteToken from './lyric-note-token';
import LyricsLine from './lyrics-line';
import LyricsVolumeIndicators from './lyrics-volume-indicators';
import { PassTheMicHandover, PassTheMicProgress, PassTheMicSymbol, SingerTag, TradingLinesOrder } from './pass-the-mic';
import { LyricsProps } from './types';
import { getBreakState, getHeadstartPercent, getPassTheMicUiState, getSingerAtSection } from './utils';

function CurrentLyricsLine(props: {
  playerNumber: number;
  section: NotesSection | null;
  currentBeat: songBeat;
  playerColor: string;
  headstartPercent: number;
  effectsEnabled: boolean;
  showSwap: boolean;
  tag: ReactNode;
}) {
  return (
    <LyricsLine layout effectsEnabled={props.effectsEnabled} data-test={`lyrics-current-player-${props.playerNumber}`}>
      <AnimatedLine
        motionKey={String(props.section?.start ?? 'no-section')}
        effectsEnabled={props.effectsEnabled}
        initial={{
          opacity: 0,
          y: 20,
          scale: 0.9,
        }}
        animate={{
          y: 0,
          opacity: 1,
          scale: 1,
        }}
        exit={{
          opacity: 0,
          y: -20,
          scale: 0.9,
        }}>
        {props.section ? (
          <>
            <span className="relative h-0">
              <Headstart color={props.playerColor} percent={props.headstartPercent} />
            </span>
            <span>
              {props.tag}
              {props.section.notes.map((note) => (
                <LyricNoteToken
                  key={note.start}
                  note={note}
                  currentBeat={props.currentBeat}
                  playerColor={props.playerColor}
                />
              ))}
              {props.showSwap && <PassTheMicSymbol active />}
            </span>
          </>
        ) : (
          <>&nbsp;</>
        )}
      </AnimatedLine>
    </LyricsLine>
  );
}

function NextLyricsLine(props: {
  playerNumber: number;
  nextSection: NotesSection | null;
  effectsEnabled: boolean;
  showSwap: boolean;
  tag: ReactNode;
}) {
  if (!props.effectsEnabled) return null;

  return (
    <LyricsLine nextLine effectsEnabled={props.effectsEnabled} data-test={`lyrics-next-player-${props.playerNumber}`}>
      {props.nextSection ? (
        <AnimatedLine
          motionKey={String(props.nextSection.start)}
          effectsEnabled={props.effectsEnabled}
          initial={{
            opacity: 0,
            y: 15,
            scale: 0.85,
          }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
          }}
          exit={{
            opacity: 0,
            y: -15,
            scale: 1.15,
          }}>
          <span>
            {props.tag}
            {props.nextSection.notes.map((note) => (
              <Fragment key={note.start}>{note.lyrics}</Fragment>
            ))}
            {props.showSwap && <PassTheMicSymbol />}
          </span>
        </AnimatedLine>
      ) : (
        <>&nbsp;</>
      )}
    </LyricsLine>
  );
}

const singerColor = playerTextColor;
const singerAtColor = (trackSingers: number[], index: number) => singerColor(trackSingers[index] ?? 0);

export default function Lyrics({
  player,
  bottom = false,
  effectsEnabled,
  showStatusForAllPlayers,
  announcesBreak = false,
}: LyricsProps) {
  'use no memo'; // React Compiler: reads GameState (current beat, section, sing setup) directly during render while receiving the same `player` object reference every tick, so the compiler's auto-memo bails out and the lyrics/pass-the-mic UI never updates.
  const playerState = GameState.getPlayer(player.number)!;
  const section = playerState.getCurrentSection();
  const nextSection = playerState.getNextSection();
  const subsequentSection = playerState.getNextSection(2);
  const currentBeat = GameState.getCurrentBeat();
  const beatLength = GameState.getSongBeatLength();
  const mode = GameState.getSingSetup()?.mode;
  const changes = playerState.getTrack().changes;

  // Whoever has a track to themselves has nobody to hand it to, so there is nothing to announce.
  const trackSingers = getTrackSingers(playerState.getTrackIndex());

  const passTheMicUi = getPassTheMicUiState({
    mode,
    changes,
    sectionStart: section?.start ?? null,
    sectionIsPause: section?.type === 'pause',
    nextSectionStart: nextSection?.start ?? null,
    subsequentSectionStart: subsequentSection?.start ?? null,
    currentBeat,
    beatLengthMs: beatLength,
    singersOnTrack: trackSingers.length,
  });

  const currentNotesSection = isNotesSection(section) ? section : null;

  const nextNotesSection = isNotesSection(nextSection) ? nextSection : null;

  // Pass the mic runs one track that everybody takes turns on, so the lyric fills in the colour of
  // whoever holds the microphone rather than the colour of the band's own player.
  const playerColor =
    mode === GAME_MODE.PASS_THE_MIC && trackSingers.length > 1
      ? singerAtColor(trackSingers, passTheMicUi.currentSinger)
      : singerColor(player.number);

  // todo these calculations should be inside Headstart component
  const beatsBetweenSectionAndNote = currentNotesSection
    ? getFirstNoteStartFromSections([currentNotesSection]) - currentNotesSection.start
    : 0;
  const headstartPercent = currentNotesSection
    ? getHeadstartPercent(currentBeat, currentNotesSection.start, beatsBetweenSectionAndNote)
    : 2;

  const singerAt = (index: number) => {
    const number = trackSingers[index] ?? 0;

    return { name: PlayersManager.getPlayer(number)?.getName() ?? `Player ${number + 1}`, color: singerColor(number) };
  };

  const { tradingLines, handingOver } = passTheMicUi;
  const breakState = effectsEnabled ? getBreakState({ section, currentBeat, beatLengthMs: beatLength }) : null;

  // The video runs on after the last note, and both bands used to empty out for it.
  const lastNotes = playerState.getLastNotesSection();
  const singingOver = effectsEnabled && !!lastNotes?.notes.length && currentBeat > getLastNoteEnd(lastNotes);

  // A break that ends on a change IS the hand-over, so the break names who is next instead of the
  // edge and the chip saying it over the top of it.
  const nextAtBreakEnd = passTheMicUi.handOverAtBreakEnd ? singerAt(passTheMicUi.nextSinger) : null;
  const nextLineSinger = getSingerAtSection(changes, nextNotesSection?.start ?? null, trackSingers.length);

  const passTheMicPresentation = tradingLines ? 'trading-lines' : handingOver ? 'handing-over' : 'off';

  // Only the lower band carries the hand-over chip and the running order: the upper one is where
  // the lyric being sung lives, and F6b keeps the announcement off it.
  const chip =
    handingOver && bottom ? (
      <PassTheMicHandover
        name={singerAt(passTheMicUi.nextSinger).name}
        color={singerAt(passTheMicUi.nextSinger).color}
        msToChange={passTheMicUi.msToChange}
      />
    ) : null;

  const showOrder = tradingLines && bottom && effectsEnabled;

  // `relative` only alongside the chip: it makes the absolutely positioned lyric lines size
  // themselves against this column instead of the whole band, which is what keeps them off the
  // right edge - but it also narrows them by the band's padding, so every other state keeps the
  // geometry it has always had.
  const currentRow = singingOver ? (
    <SingingOverNotice />
  ) : breakState && announcesBreak ? (
    <BreakAnnouncement state={breakState} next={nextAtBreakEnd} />
  ) : breakState ? (
    <BreakRest />
  ) : (
    <CurrentLyricsLine
      playerNumber={player.number}
      section={currentNotesSection}
      currentBeat={currentBeat}
      playerColor={playerColor}
      headstartPercent={headstartPercent}
      effectsEnabled={effectsEnabled}
      showSwap={passTheMicUi.showSwapOnCurrentLine}
      tag={tradingLines ? <SingerTag {...singerAt(passTheMicUi.currentSinger)} /> : null}
    />
  );

  const lines = (
    <div className={cn('min-w-0', chip && 'relative flex-1')}>
      {currentRow}

      {showOrder ? (
        <TradingLinesOrder
          singers={Array.from({ length: trackSingers.length + 1 }, (_unused, index) =>
            singerAt((passTheMicUi.currentSinger + index) % trackSingers.length),
          )}
        />
      ) : (
        <NextLyricsLine
          playerNumber={player.number}
          nextSection={nextNotesSection}
          effectsEnabled={effectsEnabled}
          showSwap={passTheMicUi.showSwapOnNextLine}
          tag={tradingLines ? <SingerTag {...singerAt(nextLineSinger)} upcoming /> : null}
        />
      )}
    </div>
  );

  return (
    <div
      className={cn(
        !effectsEnabled ? `h-8` : 'mobile:h-20 h-30',
        'box-border w-full p-3 text-center leading-none',
        // The band that is only resting steps back while the one carrying the count does the talking.
        !singingOver && breakState && !announcesBreak && !breakState.ready && 'opacity-50',
      )}
      style={{ position: 'relative', background: bottom ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.5)' }}
      data-test={`lyrics-container-player-${player.number}`}
      data-pass-the-mic={passTheMicPresentation}>
      <LyricsVolumeIndicators
        player={player}
        effectsEnabled={effectsEnabled}
        showStatusForAllPlayers={showStatusForAllPlayers}
      />

      {!singingOver && breakState && announcesBreak && <BreakProgress state={breakState} next={nextAtBreakEnd} />}

      {handingOver && bottom && (
        <>
          <div className="absolute inset-x-0 top-0 h-1" style={{ background: styles.colors.text.active }} />
          <PassTheMicProgress
            color={singerAtColor(trackSingers, passTheMicUi.currentSinger)}
            progress={passTheMicUi.progressPercent}
          />
        </>
      )}

      {chip ? (
        <div className="flex items-center gap-5">
          {chip}
          {lines}
        </div>
      ) : (
        lines
      )}
    </div>
  );
}
